<template>
  <O360Dialog
    :data-target-cancel-button="'Cancel button'"
    :cancel-button-text="t('common.cancel')"
    :data-target-confirm-button="confirmButtonDataTarget"
    :confirm-button-text="confirmButtonText"
    :is-confirm-button-disabled="isConfirmButtonDisabled"
    :is-confirm-button-loading="isConfirmButtonLoading"
    :dialog-title="dialogTitle"
    @close="emit('close')"
    @confirm="emit('confirm')"
  >
    <div class="flex flex-col gap-8">
      <div class="flex flex-col gap-4">
        <p class="font-medium">
          {{
            isSaveLayoutDialog
              ? t("charts.saveLayoutModalParagraphOne")
              : t("charts.applyLayoutModalParagraphOne")
          }}
        </p>
        <p>
          {{
            isSaveLayoutDialog
              ? t("charts.saveLayoutModalParagraphTwo")
              : t("charts.applyLayoutModalParagraphTwo")
          }}
        </p>
      </div>
      <SingleSelect
        :allow-add="isSaveLayoutDialog"
        :options="options"
        :selected="selected"
        :label="label"
        :placeholder="placeholder"
        :scrollable="true"
        :validator="validator"
        :validation-error="validationError"
        @add-custom="
          (newChartTemplate: ListItem) => emit('add-custom', newChartTemplate)
        "
        @option-selected="
          (newSelectedOpt: ListItem) => emit('option-selected', newSelectedOpt)
        "
      />
    </div>
  </O360Dialog>
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import { useI18n } from "vue-i18n";
import { O360Dialog, type ListItem } from "@airpr/360-ui-components";
import SingleSelect from "@/components/shared/SingleSelect.vue";
import { DialogType } from "@/types/ChartsType";

const { t } = useI18n();

const emit = defineEmits(["add-custom", "close", "confirm", "option-selected"]);

const props = defineProps({
  dialogType: {
    type: String as PropType<DialogType>,
    required: true,
  },
  isConfirmButtonDisabled: {
    type: Boolean,
    required: true,
  },
  isConfirmButtonLoading: {
    type: Boolean,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  options: {
    type: Array<ListItem>,
    required: true,
  },
  selected: {
    type: Object as PropType<ListItem | undefined>,
    required: true,
  },
  validator: {
    type: Function,
    default: () => true,
  },
  validationError: {
    type: Function,
    default: () => "Invalid entry",
  },
});

const isSaveLayoutDialog = computed(() => {
  return props.dialogType === "saveLayout";
});

const dialogTitle = computed(() => {
  return isSaveLayoutDialog.value
    ? t("charts.saveLayoutModalTitle")
    : t("charts.applyLayoutModalTitle");
});

const placeholder = computed(() => {
  return isSaveLayoutDialog.value
    ? t("charts.saveLayoutPlaceholder")
    : t("charts.applyLayoutPlaceholder");
});

const confirmButtonDataTarget = computed(() => {
  return isSaveLayoutDialog.value
    ? saveLayoutButtonDataTarget.value
    : "Apply Preset";
});

const confirmButtonText = computed(() => {
  return isSaveLayoutDialog.value
    ? saveLayoutButtonText.value
    : t("charts.applyPreset");
});

const saveLayoutButtonDataTarget = computed(() => {
  return props.selected && Number(props.selected?.id) > 2
    ? "Overwrite preset"
    : "Save New Preset";
});

const saveLayoutButtonText = computed(() => {
  return props.selected && Number(props.selected?.id) > 2
    ? t("charts.overwritePreset")
    : t("charts.saveNewPreset");
});
</script>
